// 脚本节点数据类型（脚本节点开发方案 §4.1）
// ScriptData 是脚本节点全部业务数据的单一数据源，持久化于 data.script；
// 生成状态字段（taskId/pendingTaskId/progress/error）沿用 BaseNodeData 顶层字段，
// 经 protectedFields GENERATION_RESULT_FIELDS 的 'script' 登记获得撤销保护。

/** 脚本节点三步骤 */
export type ScriptStep = 'SHOTS' | 'ASSETS' | 'PROMPTS';

/** 镜头/资产生成状态（与任务六态简化映射，WORKING 态支持刷新恢复） */
export type ScriptItemStatus = 'PENDING' | 'WORKING' | 'SUCCESS' | 'FAILED';

/** 分镜镜头 */
export interface Shot {
  id: string;
  /** 展示序号（1 开始，删除后重排） */
  index: number;
  /** 时长（如 "3s"） */
  duration: string;
  /** 画面描述 */
  description: string;
  /** 景别（远景/全景/中景/近景/特写） */
  shotSize: string;
  /** 灯光 */
  lighting: string;
  /** 台词 */
  dialogue: string;
  /** 音效 */
  soundEffect: string;
  /** 运镜 */
  cameraMove: string;
  /** 分镜提示词（最终提示词弹窗/合成提示词页产出，可手动改写；供文生图生成首帧画面） */
  prompt: string;
  /** v1.4.61 视频运动提示词（最终提示词弹窗产出，可手动改写；供图生视频生成运动画面） */
  motionPrompt: string;
  /** 提示词生成状态 */
  promptStatus: ScriptItemStatus;
}

/** v1.4.61 最终提示词生成模式（弹窗底栏单选）：ai=智能合成（对话模型生成，消耗积分）| local=自动拼接（本地模板，即时免费） */
export type PromptGenMode = 'ai' | 'local';

/** 资产类别 */
export type AssetKind = 'CHARACTER' | 'SCENE' | 'PROP';

/** 脚本资产（角色/场景/道具） */
export interface ScriptAsset {
  id: string;
  kind: AssetKind;
  name: string;
  description: string;
  /** v1.4.76 实体别名（同一实体的其他称谓，如本名「萧景琛」与职务尊称「摄政王」）：
   *  参与分镜高亮词典与 @-提及匹配，使别称文本也能命中对应资产；旧数据/缺省为空数组 */
  aliases?: string[];
  /** 资产图（saveAssets 落 /assets/ 的永久 URL，禁平台临时 URL） */
  imageUrl?: string;
  /** 资产图生成状态 */
  status: ScriptItemStatus;
  /** v1.4.56 生成进度文案（「生成中 32%...」，由 useScriptAssetGenSync 按耗时估算回写，终态清空） */
  progress?: string;
}

/**
 * 脚本资产生成引用（v1.4.56）：IMAGE_GENERATOR 节点由脚本面板「资产生成」创建/复用时写入 data.scriptAssetRef。
 * 全局同步 hook（useScriptAssetGenSync）据此把节点终态（SUCCESS/ERROR/取消）写回来源脚本的 script.assets；
 * 编排层 upstreamTexts 与角色参考图收集跳过此类节点，防资产生图提示词/图片污染下游分镜与生图语义。
 */
export interface ScriptAssetGenRef {
  /** 来源脚本节点 id（写回目标） */
  scriptNodeId: string;
  /** 目标资产 id（script.assets 中的 id） */
  assetId: string;
  kind: AssetKind;
  name: string;
  /** 面板所选画质档（imageGeneratorStrategy 透传 params.quality；画布常规生图无画质概念，仅资产节点携带） */
  quality?: string;
  /** 本次生成起始时间戳（进度百分比估算基准，重新生成时刷新） */
  startedAt: number;
}

/**
 * v1.4.85 分镜生图锚定引用：批量分镜生图创建 IMAGE_GENERATOR 节点时写入 data.scriptShotRef。
 * assetIds 为该镜锚定的资产 id 顺序表（顺序即提示词「@图片N」编号）；
 * imageGeneratorStrategy 经上游连线脚本节点实时解析 imageUrl 按此顺序注入 inputImages
 * （图生图参考图），资产图重生成后重跑节点自动取新图；无锚定资产时不写此字段（纯文生图）。
 */
export interface ScriptShotGenRef {
  /** 来源脚本节点 id */
  scriptNodeId: string;
  /** 来源分镜 id（script.shots 中的 id） */
  shotId: string;
  /** 锚定资产 id 顺序表（顺序即「@图片N」编号，仅含已生成资产图的资产） */
  assetIds: string[];
}

/** 脚本数据（节点 data.script） */
export interface ScriptData {
  /** 数据结构版本（未来演进迁移用） */
  dataVersion: 1;
  /** 脚本标题 */
  title: string;
  /** 数据来源：剧本生成分镜/角色生成分镜/手动编写 */
  source: 'SCRIPT' | 'CHARACTER' | 'MANUAL';
  /** 当前所在步骤 */
  currentStep: ScriptStep;
  /** 分镜镜头列表 */
  shots: Shot[];
  /** 资产列表 */
  assets: ScriptAsset[];
  /** v1.4.35 全局视觉风格（script-to-storyboard 由 LLM 从剧本提炼，ASSETS 页可改，
   *  注入资产图与单镜提示词的生图 prompt 保证全片画风统一；空串=未设置） */
  style: string;
  /** 已生成提示词的镜头数（冗余计数，加载时按 shots 重算兜底） */
  promptsGenerated: number;
}

/** 空白脚本（手动编写入口初始化用） */
export function createEmptyScript(source: ScriptData['source'], title = ''): ScriptData {
  return {
    dataVersion: 1,
    title,
    source,
    currentStep: 'SHOTS',
    shots: [],
    assets: [],
    style: '',
    promptsGenerated: 0,
  };
}

/** 防御式读取脚本数据（旧数据/损坏数据兜底空脚本） */
export function readScript(data: unknown): ScriptData | undefined {
  if (!data || typeof data !== 'object') return undefined;
  const s = data as Partial<ScriptData>;
  if (!Array.isArray(s.shots) || !Array.isArray(s.assets)) return undefined;
  return {
    dataVersion: 1,
    title: s.title ?? '',
    source: s.source ?? 'MANUAL',
    currentStep: s.currentStep ?? 'SHOTS',
    // v1.4.61 motionPrompt 旧数据兜底空串（防 undefined 传入受控组件）
    shots: s.shots.map((sh) => ({ ...sh, motionPrompt: typeof sh.motionPrompt === 'string' ? sh.motionPrompt : '' })),
    // v1.4.76 aliases 旧数据兜底空数组（过滤非字符串项，防别名词典混入脏数据）
    assets: s.assets.map((a) => ({
      ...a,
      aliases: Array.isArray(a.aliases) ? a.aliases.filter((x): x is string => typeof x === 'string') : [],
    })),
    // v1.4.35 style 旧数据兜底空串（可选注入，缺省不阻塞任何步骤门控）
    style: typeof s.style === 'string' ? s.style : '',
    promptsGenerated: typeof s.promptsGenerated === 'number' ? s.promptsGenerated : 0,
  };
}

/** v1.4.76 实体匹配词典：资产名 + 别名全部展开（trim 去空、别名与主名去重）。
 *  供分镜表 EntityText 高亮与最终提示词 @-提及使用——别称文本（如「摄政王」）也能命中资产。 */
export function collectAssetMatchNames(assets: ScriptAsset[]): string[] {
  const out: string[] = [];
  for (const a of assets) {
    const name = a.name?.trim();
    if (name) out.push(name);
    for (const alias of a.aliases ?? []) {
      const t = alias?.trim();
      if (t && t !== name) out.push(t);
    }
  }
  return out;
}

/** v1.4.76 别名 → 主资产名映射（含主名自身映射）：@-提及 hover 定位资产、名称归一用。 */
export function buildAliasNameMap(assets: ScriptAsset[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const a of assets) {
    const name = a.name?.trim();
    if (!name) continue;
    map[name] = name;
    for (const alias of a.aliases ?? []) {
      const t = alias?.trim();
      if (t) map[t] = name;
    }
  }
  return map;
}

/**
 * v1.4.85 分镜提及资产收集（全集，含未出图）：以资产名+别名词典对分镜文本（描述/台词/提示词）
 * 做包含匹配（@-提及「@资产名」同被覆盖），按 assets 原序返回。
 * 批量生图弹窗锚定段展示全集（未出图资产标注「暂无参考图」，便于解释为何未注入）。
 */
export function collectShotMentionedAssets(shot: Shot, assets: ScriptAsset[]): ScriptAsset[] {
  const text = `${shot.description}\n${shot.dialogue}\n${shot.prompt}`;
  if (!text.trim()) return [];
  return assets.filter((a) => collectAssetMatchNames([a]).some((name) => text.includes(name)));
}

/**
 * v1.4.85 分镜锚定资产收集（仅已出图）：= collectShotMentionedAssets 过滤已有 imageUrl 的子集，
 * 顺序即生图节点「@图片N」编号与 inputImages 注入顺序；批量弹窗与生图节点共用，保证「所见即所得」。
 */
export function collectShotAnchorAssets(shot: Shot, assets: ScriptAsset[]): ScriptAsset[] {
  return collectShotMentionedAssets(shot, assets).filter((a) => !!a.imageUrl);
}

/**
 * v1.4.85 资产图锚定区块：生图/生视频节点提示词头部段落。
 * v1.4.98 改名字式「@资产名」：原位置式「@图片N」编号依赖节点输入图序列（inputAssets），
 * 节点含分镜帧等额外输入图时编号整体错位（@图片1 命中首张输入图而非锚定资产），
 * 且批量弹窗 AssetMentionText 无位置解析能力；名字式在节点 PromptEditor（脚本资产表
 * 优先解析）与批量弹窗（资产名词典）两套渲染下均正确出图，与弹窗锚定行格式统一。
 * 输出形如 `资产图锚定：\n角色 现代沈昭昭 的参考图是 @现代沈昭昭\n\n`；
 * 参考图注入顺序仍由 ScriptShotGenRef.assetIds 决定（请求侧按 id 解析，不依赖提示词文本）；
 * 空数组返回空串（无锚定资产时节点提示词不带头部区块）。
 */
export function buildAssetAnchorBlock(anchorAssets: ScriptAsset[]): string {
  if (anchorAssets.length === 0) return '';
  const lines = anchorAssets.map((a) => {
    const kind = SCRIPT_ASSET_KIND_LABELS[a.kind] ?? '资产';
    return `${kind} ${a.name} 的参考图是 @${a.name}`;
  });
  return `资产图锚定：\n${lines.join('\n')}\n\n`;
}

/** 步骤中文文案（StepsBar / 卡片进度用） */
export const SCRIPT_STEP_LABELS: Record<ScriptStep, string> = {
  SHOTS: '确认镜头',
  ASSETS: '准备资产',
  PROMPTS: '合成提示词',
};

/** 资产类别中文文案（AssetsPage 分组标题 / 实体高亮悬停提示用） */
export const SCRIPT_ASSET_KIND_LABELS: Record<AssetKind, string> = {
  CHARACTER: '角色',
  SCENE: '场景',
  PROP: '道具',
};

/** v1.4.55 AI 生成参数（选择模态底栏「模型/画质/分辨率/比例」选择器 → useScriptAssets.generateOne 透传）：
 * modelId 空 = 走后端默认图像模型；quality 仅 high 显式发送（standard 走模型默认）；
 * resolution / aspectRatio 写入请求 params，由 adapter 按模型映射上游字段 */
export interface AssetGenOptions {
  modelId?: string;
  quality?: string;
  resolution?: string;
  aspectRatio?: string;
}

/** 新建空白镜头（添加镜头 / 行插入用，id 项目内时间戳唯一） */
export function createEmptyShot(index: number): Shot {
  return {
    id: `shot-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    index,
    duration: '3s',
    description: '',
    shotSize: '',
    lighting: '',
    dialogue: '',
    soundEffect: '',
    cameraMove: '',
    prompt: '',
    motionPrompt: '',
    promptStatus: 'PENDING',
  };
}

/** 新建空白资产（AssetsPage 手动新增卡片用） */
export function createEmptyAsset(kind: AssetKind, name: string): ScriptAsset {
  return {
    id: `asset-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    kind,
    name,
    description: '',
    status: 'PENDING',
  };
}
